import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  hudTemperatureText,
  hudWeather,
  hudWeatherLabel,
  readWeatherState,
} from '../state/hud-weather.js';
import { useSessionStore } from '../state/session-store.js';
import type { WeatherReading } from '../state/welcome-weather.js';
import { WeatherIcon } from './WeatherIcon.js';

/** Re-check day/night this often, so the sun turns into the moon without a new reading. */
const NIGHT_CHECK_MS = 60_000;

/** HUD box with the weather icon and temperature of the office the player is in. */
export function HudWeather() {
  const { t } = useTranslation();
  const room = useSessionStore((s) => s.room);
  const region = useSessionStore((s) => s.region);
  const [reading, setReading] = useState<WeatherReading | undefined>(() =>
    readWeatherState(room?.state, region),
  );
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    if (!room) return;
    setReading(readWeatherState(room.state, region));
    const onState = (state: unknown) => setReading(readWeatherState(state, region));
    room.onStateChange(onState);
    return () => room.onStateChange.remove(onState);
  }, [room, region]);

  useEffect(() => {
    const timer = window.setInterval(() => setNow(new Date()), NIGHT_CHECK_MS);
    return () => window.clearInterval(timer);
  }, []);

  const weather = hudWeather(now, reading);
  const temperature = hudTemperatureText(weather, t);
  return (
    <div
      className="hud-box hud-weather"
      role="img"
      aria-label={hudWeatherLabel(weather, t, region)}
    >
      <WeatherIcon kind={weather.icon} />
      {temperature && <span aria-hidden="true">{temperature}</span>}
    </div>
  );
}
